import { HTMLAttributes, TableHTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { Card } from './Card';
import { cn } from './cn';

/**
 * Table — the one way to render a data table.
 *
 * Before this primitive existed, 10 pages hand-rolled `<table>` markup with
 * 13 distinct `<th>` class strings (frontend audit, 2026-10-02). Every one of
 * them was trying to express the same thing: an elevated card, a quiet
 * uppercase header row, hairline row dividers, a hover tint. This file is
 * that thing, once.
 *
 * Usage:
 *   <Table>
 *     <THead>
 *       <TR><TH>Name</TH><TH align="right">Count</TH><TH srOnly>Actions</TH></TR>
 *     </THead>
 *     <TBody>
 *       <TR><TD>…</TD><TD align="right" mono>42</TD><TD>…</TD></TR>
 *     </TBody>
 *   </Table>
 *
 * `Table` wraps itself in a `Card` with horizontal overflow so wide tables
 * scroll inside the card instead of breaking the page. Pass `bare` to opt out
 * (e.g. when the table already sits inside a card).
 */

type Align = 'left' | 'right' | 'center';
const alignClass: Record<Align, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  /** Render without the surrounding Card (table already inside one). */
  readonly bare?: boolean;
  /** Tighter row padding for dense admin lists. */
  readonly dense?: boolean;
}

export function Table({ bare, dense, className, children, ...rest }: TableProps) {
  const table = (
    <table
      className={cn('w-full text-sm', dense && '[&_td]:py-2 [&_th]:py-2', className)}
      {...rest}
    >
      {children}
    </table>
  );
  if (bare) return table;
  return <Card className="overflow-x-auto">{table}</Card>;
}

export function THead({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('border-b border-border', className)} {...rest} />;
}

export function TBody({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('divide-y divide-border/60', className)} {...rest} />;
}

export interface TRProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Row reads as clickable (pointer + stronger hover). */
  readonly interactive?: boolean;
  /** Row is visually de-emphasised (revoked, disabled, archived). */
  readonly muted?: boolean;
}

export function TR({ interactive, muted, className, ...rest }: TRProps) {
  return (
    <tr
      className={cn(
        'group transition-colors',
        interactive ? 'cursor-pointer hover:bg-bg-hover/60' : 'hover:bg-bg-inset/40',
        muted && 'opacity-50',
        className,
      )}
      {...rest}
    />
  );
}

export interface THProps extends ThHTMLAttributes<HTMLTableCellElement> {
  readonly align?: Align;
  /** Header text exists for screen readers only (icon/action columns). */
  readonly srOnly?: boolean;
}

export function TH({ align = 'left', srOnly, className, children, ...rest }: THProps) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-3 orb-section-title font-semibold', alignClass[align], className)}
      {...rest}
    >
      {srOnly ? <span className="sr-only">{children}</span> : children}
    </th>
  );
}

export interface TDProps extends TdHTMLAttributes<HTMLTableCellElement> {
  readonly align?: Align;
  /** Tabular figures in the mono face — counts, hashes, timestamps. */
  readonly mono?: boolean;
  /** Primary cell: the row's name/identity, rendered in full foreground. */
  readonly primary?: boolean;
}

export function TD({ align = 'left', mono, primary, className, ...rest }: TDProps) {
  return (
    <td
      className={cn(
        'px-4 py-3 align-middle',
        alignClass[align],
        mono && 'font-mono text-xs tabular-nums',
        primary ? 'font-medium text-fg' : 'text-fg-muted',
        className,
      )}
      {...rest}
    />
  );
}
